/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/react'
import { TextInput } from '../ts-components/TextInput'
import { TextAreaInput } from '../ts-components/TextAreaInput'
import { SelectInput } from '../ts-components/SelectInput'
import { NumberInput } from '../ts-components/NumberInput'
import DatePicker from '../ts-components/DatePicker'
import { DateOfBirthInput } from '../ts-components/DateOfBirthInput'
import PhoneInput from '../ts-components/PhoneInput'
import { CountrySelectInput } from '../ts-components/CountrySelectInput'
import { PlayerPositionSelect } from '../ts-components/PlayerPositionSelect'
import { Checkbox } from '../ts-components/Checkbox'
import Button from '../ts-components/Button'
import colors from '@sportninja/common/constants/appColors'
import { FormGroup, FormItem, FORM_ITEM_TYPES } from '../../types/CustomForm'
import {
  filterEnabledGroups,
  sortGroupsByIndex,
  sortItemsByIndex,
  mapFormItemTypeToKind,
  extractOptions,
  extractNumberRange,
  extractMaxChar,
  formatFieldLabel,
} from '../../utils/customFormHelpers'
import {
  Container,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  QuestionsGrid,
  QuestionContainer,
  EmptyStateContainer,
  EmptyStateTitle,
  EmptyStateMessage,
  ButtonContainer,
} from './styles'
import { Dayjs } from 'dayjs'
import { toSnakeCase, sanitizeHtmlUrls } from 'src/utils/stringUtils'
import { createTabIndexGenerator } from 'src/utils/formTabIndex'

// Answer actions
export type AnswerAction =
  | { type: 'SET'; id: string; value: FieldValue }
  | { type: 'SET_ALL'; payload: AnswerState }

interface DynamicFormProps {
  /** Array of form groups (sections) to render */
  groups: FormGroup[]
  /** Sport ID for player position selection */
  sportId?: string
  /** Handler for form submission with collected answers */
  onSubmit?: (answers: AnswerState) => void
  /** Handler for back button click */
  onBack?: () => void
  /** Loading state for buttons */
  loading?: boolean
  /** Custom text for back button */
  backButtonText?: string
  /** Custom text for next button */
  nextButtonText?: string
  /** Controlled form answers state */
  answers: AnswerState
  /** Dispatch function for answer updates */
  dispatchAnswer: (action: AnswerAction) => void
  /** Hide the bottom action buttons (useful when buttons are handled externally) */
  hideButtons?: boolean
  /** Ref to expose the submit function externally */
  submitRef?: React.MutableRefObject<(() => void) | null>
  /** Field errors */
  fieldErrors: any
  /** Custom error */
  customError: any
  /** Set of field IDs that should be disabled (e.g., pre-filled reserved fields) */
  disabledFields?: Set<string>
}

// Answer state types - including arrays for checkbox/multi-select fields
export type FieldValue =
  | string
  | number
  | boolean
  | Dayjs
  | string[]
  | null
  | undefined
export type AnswerState = Record<string /* item.id */, FieldValue>

// Returns the placeholder to use for fields that support it
const getPlaceholder = (item: FormItem, kind: string) => {
  return item.description?.trim()
    ? item.description.trim() // use description when provided
    : `Enter ${item.name}${kind === 'textarea' ? '...' : ''}`
}

// Helper to determine if a field is required (handles 1/0, true/false, etc.)
function isFieldRequired(is_required: any): boolean {
  // Accepts 1, true, '1', 'true' as required
  return (
    is_required === 1 ||
    is_required === true ||
    is_required === '1' ||
    is_required === 'true'
  )
}

const TRUNCATE_LENGTH = 1000

// Content section component with read more functionality
const ContentSection: React.FC<{ content: string }> = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const shouldTruncate = content.length > TRUNCATE_LENGTH
  const displayContent =
    shouldTruncate && !isExpanded
      ? content.substring(0, TRUNCATE_LENGTH)
      : content

  return (
    <div css={SectionContainer}>
      {/* Top separator */}
      <div
        css={css`
          height: 1px;
          background: #33363b;
          margin: 0 0;
        `}
      />
      {/* Content */}
      <div
        className='rich-text-content'
        css={css`
          color: ${colors.NEUTRAL_0};
          font-family: inherit;
          line-height: 1.5;
          /* Style the rich text content for form display */
          h1,
          h2,
          h3 {
            font-weight: 600;
            color: ${colors.NEUTRAL_0};
          }

          h1 {
            font-size: 1.75em;
          }
          h2 {
            font-size: 1.5em;
          }
          h3 {
            font-size: 1.25em;
          }

          p {
            color: ${colors.NEUTRAL_0};
          }

          /* Force list styles - using pseudo-elements as workaround for global reset */
          &.rich-text-content ul {
            margin: 8px 0 !important;
            padding-left: 0 !important;
            color: ${colors.NEUTRAL_0} !important;
            list-style: none !important;
          }

          &.rich-text-content ol {
            margin: 8px 0 !important;
            padding-left: 0 !important;
            color: ${colors.NEUTRAL_0} !important;
            list-style: none !important;
            counter-reset: list-counter !important;
          }

          &.rich-text-content ul li {
            margin: 4px 0 !important;
            padding-left: 28px !important;
            color: ${colors.NEUTRAL_0} !important;
            display: block !important;
            position: relative !important;
          }

          &.rich-text-content ol li {
            margin: 4px 0 !important;
            padding-left: 28px !important;
            color: ${colors.NEUTRAL_0} !important;
            display: block !important;
            position: relative !important;
            counter-increment: list-counter !important;
          }

          /* Create custom bullet points and numbers */
          &.rich-text-content ul li::before {
            content: '•' !important;
            color: ${colors.NEUTRAL_0} !important;
            font-size: 16px !important;
            line-height: inherit !important;
            position: absolute !important;
            left: 8px !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
          }

          &.rich-text-content ol li::before {
            content: counter(list-counter) '.' !important;
            color: ${colors.NEUTRAL_0} !important;
            font-weight: bold !important;
            line-height: inherit !important;
            position: absolute !important;
            left: 8px !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            min-width: 20px !important;
          }

          strong {
            font-weight: 600;
            color: ${colors.NEUTRAL_0};
          }

          em {
            font-style: italic;
            color: ${colors.NEUTRAL_0};
          }

          u {
            text-decoration: underline;
            color: ${colors.NEUTRAL_0};
          }

          s {
            text-decoration: line-through;
            color: ${colors.NEUTRAL_0};
          }

          a {
            color: ${colors.PRIMARY};
            text-decoration: underline;
          }

          img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
          }

          blockquote {
            border-left: 4px solid ${colors.PRIMARY};
            padding-left: 16px;
            margin: 16px 0;
            font-style: italic;
            opacity: 0.9;
            color: ${colors.NEUTRAL_0};
          }

          /* Text alignment classes from Quill */
          .ql-align-left {
            text-align: left;
          }
          .ql-align-center {
            text-align: center;
          }
          .ql-align-right {
            text-align: right;
          }
          .ql-align-justify {
            text-align: justify;
          }
        `}
        dangerouslySetInnerHTML={{ __html: sanitizeHtmlUrls(displayContent) }}
      />
      {shouldTruncate && (
        <button
          css={css`
            background: none;
            border: none;
            color: ${colors.PRIMARY};
            cursor: pointer;
            padding: 8px 0;
            font-size: 14px;
            text-decoration: underline;
            &:hover {
              opacity: 0.8;
            }
          `}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </button>
      )}
      {/* Bottom separator */}
      <div
        css={css`
          height: 1px;
          background: #33363b;
          margin: 0 0;
        `}
      />
    </div>
  )
}

/**
 * DynamicForm component renders interactive custom form sections
 * Collects user input and provides form submission functionality
 * Uses the same rendering logic as DynamicFormPreview but with live interaction
 * Supports all field types including player position selection
 *
 * @param props - Component props containing form groups, sport ID, and handlers
 * @returns JSX element containing the rendered interactive form
 *
 * @example
 * ```tsx
 * <DynamicForm
 *   groups={selectedForm.groups}
 *   sportId={registration.sport_id}
 *   onSubmit={(answers) => console.log('Form submitted:', answers)}
 *   onBack={() => console.log('Back clicked')}
 *   loading={false}
 * />
 * ```
 */
const DynamicForm: React.FC<DynamicFormProps> = ({
  groups,
  sportId,
  onSubmit,
  onBack,
  loading = false,
  backButtonText = 'Back',
  nextButtonText = 'Next',
  answers,
  dispatchAnswer,
  hideButtons = false,
  submitRef,
  fieldErrors,
  customError,
  disabledFields,
}) => {
  console.log('🔄 DynamicForm: Rendering interactive form', {
    groupsCount: groups.length,
    groups,
  })

  // Generate tab indices using closure to avoid re-renders
  // This maintains sequential order across renders without triggering React state updates
  const nextTabIndex = useMemo(() => createTabIndexGenerator(), [])

  // Filter and sort groups and their items
  const enabledGroups = filterEnabledGroups(groups)
  const sortedGroups = sortGroupsByIndex(enabledGroups).map(sortItemsByIndex)

  /**
   * 🔄 getFieldLabel: Gets the appropriate label for a field, including character limits for text-based fields
   */
  const getFieldLabel = (item: FormItem, kind: string): string => {
    // Show character limits for text-based fields that support maxChar
    const textBasedFields = ['text', 'email', 'url', 'textarea']

    if (textBasedFields.includes(kind)) {
      return formatFieldLabel(item)
    }

    // For other field types, use the standard label format
    return `${item.name}${isFieldRequired(item.is_required) ? ' *' : ''}`
  }

  /**
   * 🔄 renderField: Renders the appropriate input field based on form item type
   */
  const renderField = (
    item: FormItem,
    groupName: string,
    _fieldErrors: any,
    _customError: any
  ) => {
    const kind = mapFormItemTypeToKind(item.form_item_type.id)

    const commonProps = {
      name: `form.${toSnakeCase(groupName)}.${item.id}`,
      fieldErrors: _fieldErrors,
      customError: _customError,
      label: getFieldLabel(item, kind),
      disabled: disabledFields?.has(item.id) ?? false,
      tabIndex: nextTabIndex(),
    }

    console.log('🔄 renderField: item', item)

    switch (kind) {
      case 'text': {
        const maxChar = extractMaxChar(item)
        return (
          <TextInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={getPlaceholder(item, kind)}
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'number': {
        const { min, max } = extractNumberRange(item)
        const currentValue = answers[item.id]
        const stringValue =
          typeof currentValue === 'string' || typeof currentValue === 'number'
            ? String(currentValue)
            : ''
        return (
          <NumberInput
            {...commonProps}
            value={stringValue}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            min={min}
            max={max}
            placeholder={`Enter ${item.name}`}
          />
        )
      }

      case 'dropdown': {
        const options = extractOptions(item)
        return (
          <SelectInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            onChange={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            options={options}
            placeholder={`Select ${item.name}`}
          />
        )
      }

      case 'checkbox': {
        // Normalize the value to boolean: true for true, 1, '1'; false for false, 0, '0', undefined, null, ''
        const rawValue = answers[item.id]
        const checked = rawValue === true || rawValue === 1 || rawValue === '1'

        return (
          <div
            css={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              width: '100%',
            }}
          >
            <Checkbox
              label={commonProps.label}
              name={commonProps.name}
              value={checked}
              onChange={(e) =>
                dispatchAnswer({
                  type: 'SET',
                  id: item.id,
                  value: e.target.checked,
                })
              }
              disabled={disabledFields?.has(item.id) ?? false}
              hint={item?.description ?? ''}
              fieldErrors={_fieldErrors}
              customError={_customError}
              tabIndex={commonProps.tabIndex}
              checkBoxStyle={css`
                width: auto;
                flex: 0 0 auto;
              `}
            />
          </div>
        )
      }

      case 'textarea': {
        const maxChar = extractMaxChar(item)
        return (
          <TextAreaInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={getPlaceholder(item, kind)}
            rows={3}
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'date':
        return (
          <DatePicker
            label={commonProps.label}
            value={answers[item.id] as Dayjs | null}
            onChange={(date) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: date })
            }
            tabIndex={commonProps.tabIndex}
          />
        )

      case 'birthdate': {
        // DateOfBirthInput has 3 dropdowns, so we exclude tabIndex from commonProps
        const { tabIndex: _, ...dobProps } = commonProps
        return (
          <DateOfBirthInput
            {...dobProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            // DateOfBirthInput has 3 dropdowns, so we need to assign tab indices for each
            monthTabIndex={nextTabIndex()}
            dayTabIndex={nextTabIndex()}
            yearTabIndex={nextTabIndex()}
          />
        )
      }

      case 'phone':
        return (
          <PhoneInput
            label={commonProps.label}
            value={String(answers[item.id] ?? '')}
            onChange={(v) =>
              dispatchAnswer({
                type: 'SET',
                id: item.id,
                value: v.replace(/\s+/g, ''),
              })
            }
            error={
              customError[
                `form.${toSnakeCase(groupName)}.${toSnakeCase(item.name)}`
              ]
            }
            tabIndex={commonProps.tabIndex}
            disabled={disabledFields?.has(item.id) ?? false}
          />
        )

      case 'email': {
        const maxChar = extractMaxChar(item)
        return (
          <TextInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={`Enter ${item.name}`}
            type='email'
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'url': {
        const maxChar = extractMaxChar(item)
        return (
          <TextInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={`Enter ${item.name}`}
            type='url'
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'country':
        return (
          <CountrySelectInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            onChange={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v || null })
            }
            placeholder={`Select ${item.name}`}
            tabIndex={commonProps.tabIndex}
          />
        )

      case 'playerPosition': {
        // PlayerPositionSelect needs specific props, so we extract what we need
        const { disabled: _, ...playerPositionProps } = commonProps
        return (
          <PlayerPositionSelect
            name={playerPositionProps.name}
            fieldErrors={playerPositionProps.fieldErrors}
            customError={playerPositionProps.customError}
            label={playerPositionProps.label}
            sportId={sportId}
            value={answers[item.id] as number | undefined}
            onChange={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v ?? null })
            }
            disabled={disabledFields?.has(item.id) ?? false}
            tabIndex={playerPositionProps.tabIndex}
          />
        )
      }

      default:
        return null
    }
  }

  /**
   * 🔄 handleSubmit: Handles form submission by calling onSubmit with collected answers
   */
  const handleSubmit = useCallback(() => {
    console.log('🔄 handleSubmit: Form submission triggered', { answers })
    if (onSubmit) {
      onSubmit(answers)
    }
  }, [answers, onSubmit])

  /**
   * 🔄 checkRequiredFields: Validates that all required fields have values
   * For checkboxes, they must be explicitly checked (true) to satisfy required validation
   */
  const checkRequiredFields = (): boolean => {
    const requiredFields = sortedGroups
      .flatMap((group) => group.items)
      .filter((item) => isFieldRequired(item.is_required))

    return requiredFields.every((item) => {
      const value = answers[item.id]
      if (item.form_item_type.id === FORM_ITEM_TYPES.CHECKBOX) {
        return value === true // must be checked
      }
      return value !== null && value !== undefined && value !== ''
    })
  }

  const isSubmitDisabled = loading || !checkRequiredFields()

  // Expose submit function through ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit
    }
    return () => {
      if (submitRef) {
        submitRef.current = null
      }
    }
  }, [submitRef, handleSubmit])

  // Show empty state if no enabled groups
  if (sortedGroups.length === 0) {
    return (
      <div css={Container}>
        <div css={EmptyStateContainer}>
          <div css={EmptyStateTitle}>No Form Content</div>
          <div css={EmptyStateMessage}>
            This form has no enabled sections or questions to display.
          </div>
        </div>

        {/* Buttons for empty state */}
        {!hideButtons && (onBack || onSubmit) && (
          <div css={ButtonContainer}>
            {onBack && (
              <Button
                variant='secondary'
                text={backButtonText}
                disabled={loading}
                onClick={onBack}
              />
            )}
            {onSubmit && (
              <Button
                variant='primary'
                text={nextButtonText}
                disabled={loading}
                onClick={handleSubmit}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div css={Container}>
      {sortedGroups.map((group) => {
        const isContentGroup = (group as any).is_content
        const content = (group as any).content

        if (isContentGroup && content) {
          // Render content section without header
          return <ContentSection key={group.id} content={content} />
        }

        // Render regular section with questions
        return (
          <div key={group.id} css={SectionContainer}>
            {/* Section Header */}
            <div css={SectionHeader}>
              <div css={SectionTitle}>{group.name}</div>
              {group.description && (
                <div css={SectionSubtitle}>{group.description}</div>
              )}
            </div>

            {/* Questions Grid */}
            <div css={QuestionsGrid}>
              {group.items.map((item) => {
                const fieldKind = mapFormItemTypeToKind(item.form_item_type.id)
                const isWideField = ['textarea', 'checkbox'].includes(fieldKind)

                return (
                  <div
                    key={item.id}
                    css={[
                      QuestionContainer,
                      isWideField && {
                        gridColumn: '1 / -1', // span across both columns
                      },
                    ]}
                  >
                    {renderField(
                      item,
                      group.name,
                      fieldErrors || [],
                      customError || {}
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Bottom Action Buttons */}
      {!hideButtons && (
        <div css={ButtonContainer}>
          <Button
            variant='secondary'
            text={backButtonText}
            disabled={loading}
            onClick={onBack || (() => {})}
          />
          <Button
            variant='primary'
            text={nextButtonText}
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          />
        </div>
      )}
    </div>
  )
}

export default DynamicForm
